'use client';

import { useEffect, useMemo, useState } from 'react';
import { createCommentAction, getCommentsForContent, CommentWithAuthor } from '@/app/actions/comment-actions';
import Link from 'next/link';
import { getHeaderAuthState } from '@/app/actions/auth-actions';

const renderWithHashtags = (text: string) => {
  if (!text) return null;

  const parts = text.split(/(\#[a-zA-Z0-9_]+)/g);

  return parts.map((part, index) => {
    if (/^\#[a-zA-Z0-9_]+$/.test(part)) {
      const tag = part.slice(1);
      const href = `/articles?q=${encodeURIComponent(`#${tag}`)}`;
      return (
        <Link
          key={index}
          href={href}
          className="text-[var(--color-primary)] hover:underline"
        >
          {part}
        </Link>
      );
    }

    return <span key={index}>{part}</span>;
  });
};

type CommentsThreadProps = {
  contentId: number;
  slug: string;
  initialComments: CommentWithAuthor[];
  clientFetchOnMount?: boolean;
  canComment?: boolean;
  commentAccess?: 'can_comment' | 'signed_out' | 'not_allowed';
};

export default function CommentsThread({
  contentId,
  slug,
  initialComments,
  clientFetchOnMount = false,
}: CommentsThreadProps) {
  const [comments, setComments] = useState(initialComments);
  const [canComment, setCanComment] = useState(false);
  const [commentAccess, setCommentAccess] = useState<'can_comment' | 'signed_out' | 'not_allowed'>('signed_out');
  const [body, setBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const grouped = useMemo(() => {
    const parents: CommentWithAuthor[] = [];
    const children: Record<number, CommentWithAuthor[]> = {};

    for (const c of comments) {
      if (c.parent_comment_id == null) {
        parents.push(c);
      } else {
        if (!children[c.parent_comment_id]) children[c.parent_comment_id] = [];
        children[c.parent_comment_id].push(c);
      }
    }

    return { parents, children };
  }, [comments]);

  const reloadComments = async () => {
    setLoading(true);
    try {
      const latest = await getCommentsForContent(contentId);
      setComments(latest);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const auth = await getHeaderAuthState();
        if (cancelled) return;

        const isAllowed =
          auth.kind === 'public_author' ||
          (auth.kind === 'user' && auth.user.role === 'contributor') ||
          auth.kind === 'admin';

        setCanComment(isAllowed);
        setCommentAccess(
          isAllowed
            ? 'can_comment'
            : auth.kind === 'none'
            ? 'signed_out'
            : 'not_allowed'
        );
      } catch (e) {
        console.error('Failed to fetch comment auth state', e);
      }
    })();

    if (clientFetchOnMount) {
      reloadComments();
    }

    return () => {
      cancelled = true;
    };
  }, [clientFetchOnMount, contentId]);

  const submitComment = async (e: React.FormEvent, parentId: number | null) => {
    e.preventDefault();
    if (submitting) return;

    const text = parentId ? replyBody : body;
    if (!text.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('content_id', String(contentId));
      formData.append('slug', slug);
      formData.append('body', text.trim());
      if (parentId) {
        formData.append('parent_comment_id', String(parentId));
      }

      const res = await createCommentAction(formData);
      if (!res?.success) {
        setError(res?.error || 'Unable to post comment.');
      } else {
        await reloadComments();
        setBody('');
        setReplyBody('');
        setReplyToId(null);
      }
    } catch (err) {
      console.error(err);
      setError('Unable to post comment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {canComment ? (
        <form
          onSubmit={(e) => submitComment(e, null)}
          className="glass-card border border-[var(--color-border)] rounded-2xl p-4 space-y-3"
        >
          <label htmlFor="new-comment" className="sr-only">New comment</label>
          <textarea
            id="new-comment"
            name="new_comment"
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-secondary)] px-3 py-2 text-sm text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none resize-none"
            placeholder="Write a comment..."
          />

          {error && (
            <p className="text-xs text-red-500">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="submit"
              className="btn btn-primary text-xs px-4 py-2 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={!body.trim() || submitting}
            >
              Comment
            </button>
          </div>
        </form>
      ) : (
        <div className="glass-card border border-[var(--color-border)] rounded-2xl p-4">
          <div className="text-sm text-[var(--color-text-muted)]">
            {commentAccess === 'signed_out' ? (
              <>
                Sign in to comment.
                <span className="ml-2">
                  <Link href="/login" className="text-[var(--color-primary)] hover:underline">
                    Sign in
                  </Link>
                </span>
              </>
            ) : (
              <>You can’t leave comments with this account.</>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {loading && grouped.parents.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">
            Loading comments...
          </p>
        )}

        {!loading && grouped.parents.length === 0 && (
          <p className="text-sm text-[var(--color-text-muted)]">
            No comments yet. Be the first to share your thoughts.
          </p>
        )}

        {grouped.parents.map(comment => (
          <article
            key={comment.id}
            className="flex flex-col gap-2 border-b border-[var(--color-border)] pb-4 last:border-b-0"
          >
            <div className="flex gap-3">
              <div className="h-9 w-9 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-xs font-bold text-[var(--color-text-muted)] overflow-hidden flex-shrink-0">
                {comment.author_avatar_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={comment.author_avatar_url}
                    alt={comment.author_display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>
                    {comment.author_display_name
                      .split(' ')
                      .map(part => part[0])
                      .join('')
                      .toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs mb-1">
                  {comment.author_handle ? (
                    <>
                      <Link
                        href={`/u/${comment.author_handle}`}
                        className="font-semibold text-[var(--color-text-main)] hover:underline"
                      >
                        {comment.author_display_name}
                      </Link>
                      <span className="text-[var(--color-text-muted)]">@{comment.author_handle}</span>
                    </>
                  ) : (
                    <span className="font-semibold text-[var(--color-text-main)]">
                      {comment.author_display_name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--color-text-main)] whitespace-pre-line">
                  {renderWithHashtags(comment.body)}
                </p>

                <button
                  type="button"
                  className="mt-1 text-[11px] font-medium text-[var(--color-primary)] hover:underline"
                  onClick={() => {
                    setReplyToId(comment.id);
                    setReplyBody(prev => (replyToId === comment.id ? prev : ''));
                  }}
                >
                  Reply
                </button>
              </div>
            </div>

            {grouped.children[comment.id]?.map(reply => (
              <div key={reply.id} className="ml-11 flex gap-3 mt-1">
                <div className="h-8 w-8 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-[11px] font-bold text-[var(--color-text-muted)] overflow-hidden flex-shrink-0">
                  {reply.author_avatar_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={reply.author_avatar_url}
                      alt={reply.author_display_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>
                      {reply.author_display_name
                        .split(' ')
                        .map(part => part[0])
                        .join('')
                        .toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[11px] mb-0.5">
                    {reply.author_handle ? (
                      <>
                        <Link
                          href={`/u/${reply.author_handle}`}
                          className="font-semibold text-[var(--color-text-main)] hover:underline"
                        >
                          {reply.author_display_name}
                        </Link>
                        <span className="text-[var(--color-text-muted)]">@{reply.author_handle}</span>
                      </>
                    ) : (
                      <span className="font-semibold text-[var(--color-text-main)]">
                        {reply.author_display_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--color-text-main)] whitespace-pre-line">
                    {renderWithHashtags(reply.body)}
                  </p>
                </div>
              </div>
            ))}

            {replyToId === comment.id && (
              <form
                onSubmit={(e) => submitComment(e, comment.id)}
                className="ml-11 mt-2 space-y-2"
              >
                <label htmlFor={`reply-to-${comment.id}`} className="sr-only">Reply</label>
                <textarea
                  id={`reply-to-${comment.id}`}
                  name="reply_comment"
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--bg-secondary)] px-3 py-2 text-xs text-[var(--color-text-main)] focus:border-[var(--color-primary)] focus:ring-1 focus:ring-[var(--color-primary)] outline-none resize-none"
                  placeholder="Write a reply..."
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="text-[11px] text-[var(--color-text-muted)] hover:underline"
                    onClick={() => {
                      setReplyToId(null);
                      setReplyBody('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary text-[11px] px-3 py-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={!replyBody.trim() || submitting}
                  >
                    Reply
                  </button>
                </div>
              </form>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
